import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUser } from '@fortawesome/free-solid-svg-icons';
import type { Customer, CustomerContact, CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { Modal } from '@/components/shared/Modal';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { buildAddressOptions, optionIdFor } from './address-labels';
import { resolveAddressPreview } from './address-preview';
import type { AddressSide } from './address-side';

/**
 * Choosing an address, by comparing them rather than paging through them.
 *
 * The user has already said which card they are changing, so this shows the
 * candidates directly — no second dropdown, no nested navigation. Every option
 * is rendered in full, because an operator confirming a shipping address is
 * comparing street lines, not names.
 *
 * Options are resolved through the same function that feeds the intake cards,
 * so what is shown here is exactly what gets frozen on save.
 */

export type PickTarget = 'location' | 'contact' | 'return' | 'invoice';

/** Titles mirror the card that opened the modal. */
const TITLES: Record<PickTarget, string> = {
  location: 'Change Location',
  contact: 'Change Contact Person',
  return: 'Change Shipping Address',
  invoice: 'Change Billing Address',
};

interface Choice {
  id: string;
  title: string;
  lines: string[];
  gst?: string | null;
  /** Everything searchable about this option, pre-lowercased. */
  haystack: string;
  apply: () => void;
}

export function AddressPickModal({
  target,
  customer,
  locations,
  contacts,
  locationId,
  contactId,
  billTo,
  shipTo,
  onLocationChange,
  onContactChange,
  onReturnChange,
  onInvoiceChange,
  onAddLocation,
  onAddContact,
  onClose,
}: {
  target: PickTarget;
  customer: Customer | null;
  locations: CustomerLocation[];
  contacts: CustomerContact[];
  locationId: string;
  contactId: string;
  billTo: AddressSelection;
  shipTo: AddressSelection;
  onLocationChange: (id: string) => void;
  onContactChange: (id: string) => void;
  onReturnChange: (s: AddressSelection) => void;
  onInvoiceChange: (s: AddressSelection) => void;
  onAddLocation: () => void;
  onAddContact: () => void;
  onEditContact: (c: CustomerContact) => void;
  onClose: () => void;
}) {
  const isContact = target === 'contact';
  const side: AddressSide = target === 'invoice' ? 'Bill-To' : 'Ship-To';

  const choices = useMemo<Choice[]>(() => {
    if (isContact) {
      return contacts.map((c) => {
        const title = `${c.firstName} ${c.lastName}`.trim();
        const lines = [c.phone, c.email, c.designation].filter((v): v is string => !!v);
        return {
          id: c.id,
          title,
          lines,
          haystack: [title, ...lines].join(' ').toLowerCase(),
          apply: () => onContactChange(c.id),
        };
      });
    }

    if (target === 'location') {
      return locations.map((l) => {
        const lines = [
          l.addressLine1 ?? l.address,
          l.addressLine2,
          [l.city, l.state, l.postalCode].filter(Boolean).join(', '),
          l.country,
          l.phone,
        ].filter((v): v is string => !!v);
        return {
          id: l.id,
          title: l.name,
          lines,
          haystack: [l.name, ...lines].join(' ').toLowerCase(),
          apply: () => onLocationChange(l.id),
        };
      });
    }

    // Shipping / billing: the company's own address plus every branch, resolved
    // through the shared previewer so the modal cannot drift from the card.
    return buildAddressOptions(customer, locations).map((o) => {
      const p = resolveAddressPreview(side, o.selection, customer, locations);
      const lines = p
        ? [
            p.line1,
            p.line2,
            [p.city, p.state, p.postalCode].filter(Boolean).join(', '),
            p.country,
            p.phone,
          ].filter((v): v is string => !!v)
        : [];
      // GSTIN belongs to billing and appears nowhere else.
      const gst = p && 'gstNumber' in p ? p.gstNumber : null;
      return {
        id: o.id,
        title: p?.label ?? o.name,
        lines,
        gst,
        haystack: [o.name, ...lines, gst ?? ''].join(' ').toLowerCase(),
        apply: () =>
          target === 'invoice' ? onInvoiceChange(o.selection) : onReturnChange(o.selection),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, customer, locations, contacts]);

  const currentId = isContact
    ? contactId
    : target === 'location'
      ? locationId
      : optionIdFor(target === 'invoice' ? billTo : shipTo);

  const [selectedId, setSelectedId] = useState(currentId);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? choices.filter((c) => c.haystack.includes(q)) : choices;
  }, [choices, query]);

  const confirm = () => {
    const choice = choices.find((c) => c.id === selectedId);
    if (!choice) return;
    choice.apply();
    onClose();
  };

  const addLabel = isContact ? 'Add Contact' : 'Add Address';
  const onAdd = isContact ? onAddContact : onAddLocation;

  return (
    <Modal open onClose={onClose} title={TITLES[target]} size="xl">
      <div className="flex max-h-[70vh] flex-col gap-4">
        {/* Fixed while the list below scrolls, so search is always reachable. */}
        <Input
          value={query}
          autoFocus
          placeholder={isContact ? 'Search people…' : 'Search address…'}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {choices.length === 0
                ? isContact
                  ? 'Nobody on file at this location yet.'
                  : 'No addresses on file for this customer yet.'
                : 'Nothing matches that search.'}
            </p>
          ) : (
            // Two across on a 900px modal: the whole set fits on one screen far
            // more often, which is what makes comparison possible.
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {filtered.map((c) => {
                const chosen = c.id === selectedId;
                return (
                  <label
                    key={c.id}
                    className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                      chosen ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      name="address-choice"
                      className="mt-1 h-4 w-4 shrink-0 accent-primary"
                      checked={chosen}
                      onChange={() => setSelectedId(c.id)}
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                        {isContact && <FontAwesomeIcon icon={faUser} className="h-3 w-3" />}
                        {c.title}
                      </span>
                      <span className="mt-0.5 block text-[13px] leading-relaxed text-muted-foreground">
                        {c.lines.map((l, i) => (
                          <span key={i} className="block">
                            {l}
                          </span>
                        ))}
                        {c.gst && (
                          <span className="mt-1 block font-medium text-foreground">
                            GSTIN: {c.gst}
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={onAdd}>
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-3 w-3" />
            {addLabel}
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={confirm} disabled={!selectedId}>
              Select
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
